import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { ButtonSizes } from '@x/ecommerce-shop/app/core/utils/directives/button-size.directive';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

export type AddToCartMode = 'with-controls' | 'controls-only' | 'no-controls';

@Component({
  selector: 'x-add-to-cart-button',
  templateUrl: './add-to-cart-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-add-to-cart-button' },
  preserveWhitespaces: false,
})
export class AddToCartButtonComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private _quantity: number;
  @Input() set quantity(q: number) {
    this._quantity = q;
    this.quantityControl.patchValue(q, { emitEvent: false });
  }

  get quantity() {
    return this._quantity;
  }

  @Input() buttonSize: ButtonSizes = 'medium';
  @Input() label: string = 'Add to Bag';
  @Input() loading: boolean = false;
  @Input() disabled: boolean = false;
  @Input() stackQuantity: boolean = true;
  @Input() buttonColor: ThemePalette = 'primary';
  @Input() showControlLabel: boolean = true;
  @Input() buttonType: 'stroked' | 'filled' = 'filled';

  @HostBinding('class')
  @Input()
  mode: AddToCartMode | undefined | null = 'no-controls';

  quantityControl = new UntypedFormControl();
  itemCountControl = new UntypedFormControl(1);

  _disabledTooltip: string;
  @Input() set disabledTooltip(tooltip: string | undefined) {
    if (!tooltip) return;
    this._disabledTooltip = tooltip;
  }

  @Output()
  quantityChanged = new EventEmitter<number>();

  quantityDiff$ = new BehaviorSubject<number>(0);
  pendingQuantity = 0;

  set itemCount(count: number) {
    this.itemCountControl.patchValue(count, { emitEvent: false });
  }

  get itemCount() {
    return Number(this.itemCountControl.value);
  }

  constructor(private readonly changeRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subscription.add(
      this.quantityDiff$
        .pipe(
          filter((q) => q !== 0),
          debounceTime(300), // Reduced debounce time for better responsiveness
        )
        .subscribe((diff) => {
          const desiredQuantity = Math.max(this.quantity + diff, 0);
          this.quantityDiff$.next(0);
          this.pendingQuantity = 0;
          this.changeRef.markForCheck();
          this.quantityChanged.next(desiredQuantity);
          this.itemCount = 1;
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  add(quantity = 1) {
    if (!this.stackQuantity) {
      this.quantityChanged.emit(quantity);
      return;
    }

    const newQuantity = this.quantityDiff$.value + quantity;
    this.quantityDiff$.next(newQuantity);
    
    // Update pending quantity for visual feedback
    this.pendingQuantity += quantity;
    this.changeRef.markForCheck();
  }

  remove(quantity = 1) {
    if (this.quantity + (this.quantityDiff$.value - quantity) >= 0) {
      this.quantityDiff$.next(this.quantityDiff$.value - quantity);
      this.pendingQuantity -= quantity;
      this.changeRef.markForCheck();
    }
  }

  increaseTotal() {
    this.itemCount++;
  }

  decreaseTotal() {
    if (this.itemCount < 2) return;
    this.itemCount--;
  }

  addAllItems() {
    this.quantityDiff$.next(this.quantityDiff$.value + this.itemCount);
    this.pendingQuantity += this.itemCount;
    this.changeRef.markForCheck();
  }
}
